import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BookOpen, Plus } from 'lucide-react';
import { centerAPI, courseAPI, getErrorMessage } from '../services/api';

const emptyForm = { name: '', code: '', description: '', fee: '', academic_term: '', center_id: '', is_active: true };

export default function CourseList() {
  const [courses, setCourses] = useState([]);
  const [centers, setCenters] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [coursesRes, centersRes] = await Promise.all([
        courseAPI.listCourses(),
        centerAPI.listCenters(),
      ]);
      setCourses(coursesRes.data.items || []);
      setCenters(centersRes.data.items || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.fee) {
      toast.error('Name and fee are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        code: form.code.trim() || null,
        description: form.description.trim() || null,
        fee: Number(form.fee),
        academic_term: form.academic_term.trim() || null,
        center_id: form.center_id || null,
        is_active: form.is_active,
      };
      if (editingId) {
        await courseAPI.updateCourse(editingId, payload);
        toast.success('Course updated');
      } else {
        await courseAPI.createCourse(payload);
        toast.success('Course created');
      }
      setForm(emptyForm);
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Courses</h1>
        <p className="text-gray-600 mt-1">Set course fees — parents pay the assigned amount automatically</p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
        <h2 className="text-lg font-semibold">{editingId ? 'Edit Course' : 'Create Course'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input className="border rounded-lg px-3 py-2" placeholder="Course name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className="border rounded-lg px-3 py-2" placeholder="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <input className="border rounded-lg px-3 py-2" type="number" step="0.01" placeholder="Fee (Birr) *" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} />
          <input className="border rounded-lg px-3 py-2" placeholder="Academic term (e.g. 2025 Term 1)" value={form.academic_term} onChange={(e) => setForm({ ...form, academic_term: e.target.value })} />
          <select className="border rounded-lg px-3 py-2" value={form.center_id} onChange={(e) => setForm({ ...form, center_id: e.target.value })}>
            <option value="">All centers</option>
            {centers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <textarea className="w-full border rounded-lg px-3 py-2" rows={2} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg">
          <Plus className="w-4 h-4" />
          {editingId ? 'Save' : 'Create Course'}
        </button>
      </form>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin h-10 w-10 border-b-2 border-blue-500 rounded-full" /></div>
      ) : (
        <div className="space-y-3">
          {courses.map((course) => (
            <div key={course.id} className="bg-white border rounded-lg p-4 flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold">{course.name}</h3>
                  <span className="text-sm font-medium text-green-700">{Number(course.fee).toFixed(2)} Birr</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">{course.center_name || 'All centers'} · {course.academic_term || 'No term set'} · {course.is_active ? 'Active' : 'Inactive'}</p>
              </div>
              <button onClick={() => { setEditingId(course.id); setForm({ name: course.name, code: course.code || '', description: course.description || '', fee: String(course.fee), academic_term: course.academic_term || '', center_id: course.center_id || '', is_active: course.is_active }); }} className="text-sm text-blue-600">Edit</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
